import { cn } from "@/utils/cn";

interface UsageBarProps {
  label: string;
  icon: string;
  /** Total observé dans l'organisation (cumul de l'agence). */
  used: number;
  limite: number | null;
  /**
   * Le quota de cette ressource est appliqué PAR propriétaire géré (immeubles,
   * biens, locataires, contrats côté agence), et non globalement. Dans ce cas,
   * `used` n'est qu'informatif (cumul de l'agence) et ne doit pas être comparé à
   * `limite` : on affiche donc la limite « par propriétaire » et le total à titre
   * indicatif, au lieu d'une barre de progression trompeuse.
   */
  perOwner?: boolean;
}

export default function UsageBar({ label, icon, used, limite, perOwner = false }: UsageBarProps) {
  const illimite = limite === null;
  const pct = illimite ? 0 : Math.min(100, Math.round((used / Math.max(1, limite)) * 100));
  const sature = !illimite && pct >= 90;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm text-foreground-800">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
            <i className={cn(icon, "text-sm")} aria-hidden="true" />
          </span>
          {label}
        </span>
        {perOwner ? (
          <span className="shrink-0 text-sm font-label font-medium text-foreground-950">
            {illimite ? "Illimité" : `Max ${limite}`} <span className="text-foreground-400">/ propriétaire</span>
          </span>
        ) : (
          <span className="shrink-0 text-sm font-label font-medium text-foreground-950">
            {used} <span className="text-foreground-400">/ {illimite ? "Illimité" : limite}</span>
          </span>
        )}
      </div>
      {perOwner ? (
        <p className="flex items-start gap-1.5 text-xs text-foreground-500">
          <i className="ri-information-line mt-0.5 text-sm text-foreground-400" aria-hidden="true" />
          <span>Total de l'agence : {used}. La limite s'applique à chaque propriétaire géré, indépendamment.</span>
        </p>
      ) : illimite ? (
        <div className="h-1.5 w-full rounded-full bg-secondary-100" aria-hidden="true" />
      ) : (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-100">
          <div
            className={cn("h-full rounded-full transition-all duration-500", sature ? "bg-accent-500" : "bg-primary-500")}
            style={{ width: `${pct}%` }}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}